<template>
  <el-container class="feed-card">
    <el-main class="shadow">
      <el-card>
        <template #header>
          <div
            class="header-container"
            :class="deviceClass"
            @click.self="collapse"
          >
            <div class="feed-title">
              <el-icon class="arrow" @click.self="collapse">
                <ArrowDownElementIcon v-if="closed" @click.self="collapse" />
                <ArrowLeftElementIcon v-else @click.self="collapse" />
              </el-icon>
              <span class="feed-id" @click.self="collapse">
                {{ feedFormat.id }}.
              </span>
              <span class="feed-name" @click.self="collapse">
                {{ feedFormat.name }}
              </span>
              <ProtocolLabel
                :key="feedFormat.type"
                :type="feedFormat.type"
                @click.self="collapse"
              />
              <AdTypeLabel :type="feedFormat.type_ids" @click.self="collapse" />
              <BannerSize
                v-if="AdType.Banner === feedFormat.type_ids"
                :key="id"
                :size="feedFormat.size"
                class="ml-1"
                @click.self="collapse"
              />
            </div>
            <IntegrationActions
              :integration="feedFormat"
              :show-delete="isStaff"
            />
          </div>
        </template>
        <IntegrationsList
          v-if="feedIds.length > 0"
          :feeds-ids="feedIds"
          :show-delete="isStaff"
          class="creatives-list ml-3"
          :class="{ active: !closed }"
        />
        <div v-else v-show="!closed" class="empty ml-3">No feeds yet</div>
      </el-card>
    </el-main>
  </el-container>
</template>

<script lang="ts">
import { defineComponent, computed } from 'vue'
import { useFeedFormatsStore } from '@/apps/traforama/store/feedFormats'
import useResize from '@/hooks/useResize'
import { AdType } from '@/api/types'

import IntegrationsList from './IntegrationsList.vue'
import IntegrationActions from './IntegrationActions.vue'
import BannerSize from '@/apps/traforama/components/BannerSize.vue'
import AdTypeLabel from '@/apps/traforama/components/AdTypeLabel.vue'
import ProtocolLabel from '../../components/ProtocolLabel.vue'
import { useUserStore } from '@/store/modules/user'

export default defineComponent({
  name: 'IntegrationsCard',
  components: {
    IntegrationActions,
    IntegrationsList,
    ProtocolLabel,
    BannerSize,
    AdTypeLabel
  },
  props: {
    feedFormatId: {
      type: String,
      required: true
    }
  },
  setup(props) {
    const feedFormatsStore = useFeedFormatsStore()
    const userStore = useUserStore()

    const { isStaff } = userStore
    const { deviceClass } = useResize()
    feedFormatsStore.ToggleClose({ id: props.feedFormatId, isClosed: true })

    const feedFormat = computed(() => {
      const feedFormat = feedFormatsStore.items[props.feedFormatId]
      return feedFormat?.attributes
    })

    const feedIds = computed(() => {
      const integrationFormat = feedFormatsStore.items[props.feedFormatId]
      if (feedFormat.value !== undefined) {
        const feeds = integrationFormat.relationships
          ? integrationFormat.relationships.feeds.data
          : []
        return feeds.map((entry) => entry.id)
      }
      return []
    })

    const invoke = async (event: string) => {
      let result
      switch (event) {
        case 'destroy':
          result = await feedFormatsStore.DestroyItem(props.feedFormatId)
          break
      }
    }

    const checked = computed(() => {
      return feedFormatsStore.selected[props.feedFormatId] !== undefined
    })

    const closed = computed({
      get: () => {
        return feedFormatsStore.closed[props.feedFormatId] !== undefined
      },
      set: (closed: boolean) => {
        feedFormatsStore.ToggleClose({
          id: props.feedFormatId,
          isClosed: closed
        })
      }
    })

    const collapse = () => {
      closed.value = !closed.value
    }

    return {
      feedFormat,
      feedIds,
      deviceClass,
      checked,
      AdType,
      isStaff,
      collapse,
      closed,
      invoke
    }
  }
})
</script>

<style lang="scss" scoped>
.header-container {
  display: flex;
  justify-content: space-between;
  align-items: center;

  .mr-1 {
    margin-right: 1em;
  }

  &.mobile {
    flex-direction: column;
    align-items: flex-start;
  }
}

.feed-id {
  color: var(--el-text-color-secondary);
  font-size: 12px;
}

.stream-icon {
  margin-left: 0.5em;
  color: var(--el-text-color-secondary);
}

.feed-card {
  margin-bottom: 10px;

  :deep(.el-card__header) {
    padding: 5px 20px 5px 0;

    .arrow {
      margin: 5px;
    }
  }

  :deep(.el-card__body) {
    padding: 0 5px;
  }

  .el-main {
    padding: 2px 20px;
  }

  :deep(.creatives-list) {
    transition: height 1s ease-out;
    opacity: 0;
    height: 0;
    overflow: hidden;
  }

  :deep(.creatives-list.active) {
    opacity: 1;
    height: auto;
    transition: height 1s;
  }

  :deep(.feed-name) {
    font-weight: 600;
    font-size: 14px;
  }

  .empty {
    padding: 5px;
    color: var(--el-text-color-secondary);
  }
}
</style>
